import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React from 'react';
import {
  Badge,
  Checkbox,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import {
  DRAG_HANDLE_RESERVE,
  PAR_COLUMN_WIDTH,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { OrderGuideParInput } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-par-input';
import { Pressable, ScrollView, View } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';

const NO_PAR_LEVEL = '—';

const formatParLevel = (parLevel: number | null): string =>
  parLevel === null ? '' : String(parLevel);

const PACK_DETAIL_SEPARATOR = ' · ';
const PAR_CHIP_SIZE = 18;
const ITEM_IMAGE_SIZE = Spacing.SPACE_2XLARGE;

interface OrderGuideItemRowProps {
  isEditable: boolean;
  isExpanded: boolean;
  onCommitParLevel: (variantPackUUID: string, parLevel: number | null) => void;
  onToggleExpanded: (itemUUID: string) => void;
  /** Tri-state group toggle: select/clear all of a multi-pack item's packs. */
  onToggleSelectGroup?: (packs: OrderGuideItem[]) => void;
  /** Toggle one variant pack's selection. */
  onToggleSelectPack?: (variantPackUUID: string, itemUUID: string) => void;
  packs: OrderGuideItem[];
  selectedPackUUIDs: ReadonlySet<string>;
}

const selectItemLabel = (name: string): string =>
  i18n.t('sales_portal.order_guides_list.selection.select_item', { name });

const OrderGuideItemRowComponent: React.FC<OrderGuideItemRowProps> = ({
  isEditable,
  isExpanded,
  onCommitParLevel,
  onToggleExpanded,
  onToggleSelectGroup,
  onToggleSelectPack,
  packs,
  selectedPackUUIDs,
}) => {
  const [styles, theme] = useStyles();

  const rotationProgress = useDerivedValue(() =>
    withTiming(isExpanded ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(rotationProgress.value, [0, 1], [0, 180])}deg` },
    ],
  }));

  const item = packs[0];
  const { displayName, externalID, imageURL, packDisplayString } = item;
  const isMultiPack = packs.length > 1;
  const isSelectable = !!onToggleSelectPack;
  // The drag handle floats over the row's right edge (see SortableListItem), so
  // inset the par column by its reserved width to keep them from overlapping.
  const handleReserveSpacing = isEditable
    ? theme.sk.spacing.spaceSmall + DRAG_HANDLE_RESERVE
    : theme.sk.spacing.spaceSmall;

  const itemImage = (
    <ItemImage
      alt={displayName}
      cachePolicy="memory-disk"
      imageURLs={imageURL ? [imageURL] : []}
      size={ITEM_IMAGE_SIZE}
    />
  );

  if (!isMultiPack) {
    const packDetails = [packDisplayString, externalID]
      .filter(Boolean)
      .join(PACK_DETAIL_SEPARATOR);
    const isSelected = selectedPackUUIDs.has(item.variantPackUUID);

    return (
      <XStack
        alignItems="center"
        backgroundColor={
          isSelected ? theme.sk.color.bg.primary.hover : 'transparent'
        }
        gap={theme.sk.spacing.spaceSMedium}
        hoverStyle={{
          backgroundColor: isSelectable
            ? theme.sk.color.bg.primary.hover
            : theme.sk.color.bg.primary.default,
        }}
        justifyContent="space-between"
        paddingRight={handleReserveSpacing}
      >
        <Pressable
          accessibilityLabel={selectItemLabel(displayName)}
          accessibilityRole="checkbox"
          aria-checked={isSelected}
          disabled={!isSelectable}
          onPress={
            onToggleSelectPack
              ? () => onToggleSelectPack(item.variantPackUUID, item.itemUUID)
              : undefined
          }
          style={styles.selectTarget}
        >
          {isSelectable ? (
            <Checkbox
              checked={isSelected}
              marginLeft={theme.sk.spacing.spaceSmall}
              marginRight={theme.sk.spacing.spaceXsmall}
              pointerEvents="none"
              size="$sm"
              tabIndex={-1}
            />
          ) : null}
          {itemImage}
          <YStack gap="$2" style={styles.identity}>
            <Typography
              color={theme.sk.color.text.primary}
              fontWeight="$medium"
              size="$sm"
            >
              {displayName}
            </Typography>
            {packDetails ? (
              <Typography color={theme.sk.color.text.secondary} size="$xs">
                {packDetails}
              </Typography>
            ) : null}
          </YStack>
        </Pressable>
        <OrderGuideParInput
          isEditable={isEditable}
          item={item}
          onCommitParLevel={onCommitParLevel}
        />
      </XStack>
    );
  }

  const packCountLabel = i18n.t(
    'sales_portal.order_guides_list.items.pack_count_plural',
    { count: packs.length },
  );
  const groupSelectedCount = packs.filter((pack) =>
    selectedPackUUIDs.has(pack.variantPackUUID),
  ).length;
  const groupAllSelected = groupSelectedCount === packs.length;
  const groupSomeSelected = groupSelectedCount > 0;

  return (
    <View>
      <XStack
        alignItems="center"
        backgroundColor={
          groupSomeSelected ? theme.sk.color.bg.primary.hover : 'transparent'
        }
        gap={theme.sk.spacing.spaceSMedium}
        hoverStyle={{ backgroundColor: theme.sk.color.bg.primary.hover }}
        paddingLeft={theme.sk.spacing.spaceSmall}
      >
        {isSelectable ? (
          <Checkbox
            accessibilityLabel={selectItemLabel(displayName)}
            checked={
              groupAllSelected
                ? true
                : groupSomeSelected
                  ? 'indeterminate'
                  : false
            }
            marginRight={theme.sk.spacing.spaceXsmall}
            onCheckedChange={() => onToggleSelectGroup?.(packs)}
            size="$sm"
          />
        ) : null}
        <Pressable
          accessibilityLabel={`${displayName}, ${packCountLabel}`}
          accessibilityRole="button"
          aria-expanded={isExpanded}
          onPress={() => onToggleExpanded(item.itemUUID)}
          style={styles.expandPressable}
        >
          <XStack
            alignItems="center"
            gap={theme.sk.spacing.spaceSmall}
            justifyContent="space-between"
            paddingRight={handleReserveSpacing}
            style={styles.headerRow}
          >
            <XStack
              alignItems="center"
              gap={theme.sk.spacing.spaceSMedium}
              paddingVertical="$10"
              style={styles.identity}
            >
              {itemImage}
              <YStack gap="$2" style={styles.identity}>
                <XStack alignItems="center" gap={theme.sk.spacing.spaceSMedium}>
                  <Typography
                    color={theme.sk.color.text.primary}
                    fontWeight="$medium"
                    size="$sm"
                  >
                    {displayName}
                  </Typography>
                  <Badge color="muted">{packCountLabel}</Badge>
                </XStack>
                {isExpanded ? (
                  <Typography
                    color={theme.sk.color.text.secondary}
                    size="$xs"
                    textAlign="left"
                  >
                    {i18n.t(
                      'sales_portal.order_guides_list.items.variant_packs_subtitle',
                    )}
                  </Typography>
                ) : (
                  <XStack
                    alignItems="center"
                    gap={theme.sk.spacing.spaceXsmall}
                  >
                    <Typography
                      color={theme.sk.color.text.secondary}
                      size="$xs"
                    >
                      {i18n.t(
                        'sales_portal.order_guides_list.items.pars_label',
                      )}
                    </Typography>
                    <ScrollView
                      contentContainerStyle={styles.parChips}
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      style={styles.parChipsScroll}
                    >
                      {packs.map((pack) => {
                        const accessibilityLabel = `${pack.packDisplayString}, ${i18n.t(
                          'par_level',
                          { number: pack.parLevel ?? '' },
                        )}`;
                        if (pack.parLevel === null) {
                          return (
                            <View
                              accessibilityLabel={accessibilityLabel}
                              accessible
                              key={pack.variantPackUUID}
                              style={styles.emptyParChip}
                            >
                              <Typography color="$textTertiary" size="$xs">
                                {NO_PAR_LEVEL}
                              </Typography>
                            </View>
                          );
                        }
                        return (
                          <View
                            accessibilityLabel={accessibilityLabel}
                            accessible
                            key={pack.variantPackUUID}
                            style={styles.parChip}
                          >
                            <Typography
                              color={theme.sk.color.text.primary}
                              size="$xs"
                            >
                              {formatParLevel(pack.parLevel)}
                            </Typography>
                          </View>
                        );
                      })}
                    </ScrollView>
                  </XStack>
                )}
              </YStack>
            </XStack>
            <View style={styles.expandRowsIcon}>
              <Animated.View style={chevronStyle}>
                <Icons.ChevronDown
                  color={theme.sk.color.icon.tertiary}
                  size="$14"
                />
              </Animated.View>
            </View>
          </XStack>
        </Pressable>
      </XStack>
      {isExpanded
        ? packs.map((pack) => {
            const isPackSelected = selectedPackUUIDs.has(pack.variantPackUUID);

            return (
              <XStack
                alignItems="center"
                backgroundColor={
                  isPackSelected
                    ? theme.sk.color.bg.primary.hover
                    : 'transparent'
                }
                borderTopColor={theme.sk.color.border.primary}
                borderTopWidth={1}
                gap={theme.sk.spacing.spaceSMedium}
                hoverStyle={{
                  backgroundColor: isSelectable
                    ? theme.sk.color.bg.primary.hover
                    : theme.sk.color.bg.primary.default,
                }}
                justifyContent="space-between"
                key={pack.variantPackUUID}
                paddingLeft={theme.sk.spacing.spaceSmall}
                paddingRight={handleReserveSpacing}
              >
                <Pressable
                  accessibilityLabel={selectItemLabel(pack.packDisplayString)}
                  accessibilityRole="checkbox"
                  aria-checked={isPackSelected}
                  disabled={!isSelectable}
                  onPress={
                    onToggleSelectPack
                      ? () =>
                          onToggleSelectPack(
                            pack.variantPackUUID,
                            pack.itemUUID,
                          )
                      : undefined
                  }
                  style={styles.selectTarget}
                >
                  <XStack
                    gap={theme.sk.spacing.spaceSMedium}
                    style={styles.subRowIndent}
                  >
                    {isSelectable ? (
                      <Checkbox
                        checked={isPackSelected}
                        pointerEvents="none"
                        size="$sm"
                        tabIndex={-1}
                      />
                    ) : null}
                    <YStack flexShrink={1} gap="$2">
                      <Typography
                        color={theme.sk.color.text.primary}
                        size="$sm"
                      >
                        {pack.packDisplayString}
                      </Typography>
                      {pack.externalID ? (
                        <Typography
                          color={theme.sk.color.text.secondary}
                          size="$xs"
                        >
                          {pack.externalID}
                        </Typography>
                      ) : null}
                    </YStack>
                  </XStack>
                </Pressable>
                <OrderGuideParInput
                  isEditable={isEditable}
                  item={pack}
                  onCommitParLevel={onCommitParLevel}
                />
              </XStack>
            );
          })
        : null}
    </View>
  );
};

// The selection Set's reference changes on every toggle; compare only THIS
// row's packs' membership so a toggle re-renders only the rows that changed.
const arePropsEqual = (
  prev: OrderGuideItemRowProps,
  next: OrderGuideItemRowProps,
): boolean =>
  prev.isEditable === next.isEditable &&
  prev.isExpanded === next.isExpanded &&
  prev.packs === next.packs &&
  prev.onCommitParLevel === next.onCommitParLevel &&
  prev.onToggleExpanded === next.onToggleExpanded &&
  prev.onToggleSelectGroup === next.onToggleSelectGroup &&
  prev.onToggleSelectPack === next.onToggleSelectPack &&
  prev.packs.every(
    (pack) =>
      prev.selectedPackUUIDs.has(pack.variantPackUUID) ===
      next.selectedPackUUIDs.has(pack.variantPackUUID),
  );

export const OrderGuideItemRow = React.memo(
  OrderGuideItemRowComponent,
  arePropsEqual,
);

const useStyles = createStyles((theme) => ({
  emptyParChip: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderStyle: 'dashed',
    borderWidth: 1,
    height: PAR_CHIP_SIZE,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  expandPressable: {
    flex: 1,
  },
  expandRowsIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: PAR_COLUMN_WIDTH,
  },
  headerRow: {
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  identity: {
    flexShrink: 1,
  },
  parChip: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.hover,
    borderRadius: theme.sk.radii.radiusSmall,
    height: PAR_CHIP_SIZE,
    justifyContent: 'center',
    width: PAR_CHIP_SIZE,
  },
  parChips: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceXsmall,
  },
  parChipsScroll: {
    flexShrink: 1,
  },
  selectTarget: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    gap: theme.sk.spacing.spaceSMedium,
    paddingVertical: 10,
  },
  subRowIndent: {
    paddingLeft:
      theme.sk.spacing[ITEM_IMAGE_SIZE] + theme.sk.spacing.spaceSMedium,
  },
}));
